import { BAR_SLOTS, type BarSlot, type Rules } from "odyssey-shared";
import type { LayerHandle, LayerOptions } from "../../ui/input/router.ts";
import type { DropTone } from "../../ui/kit/index.ts";
import type { ToolBelt } from "../belt/store.ts";
import type { ArrangedBar } from "./arranged.ts";
import { PageFollow, SWAP_TARGET, type ArrangingPages } from "./page-follow.ts";

/**
 * Arranging the bar on the page (P2.8-T29, ADR-121 decision 3; the author's words of 2026-10-09: "Can we also do the drag
 * behaviour on the hotbar, like we did in the inventory. This bar should also be able to be restructured."): what a drag
 * from the bar or the bag, or the keyboard's placing, does to the player's bar, and what the bar draws while one runs.
 *
 * - **From the bar.** A slot holding something is picked up; dropped on another slot the two swap, dropped anywhere off
 *   the bar an item clears and an ability or a blow is put back (decision 3.1, ADR-121's note of 2026-10-10).
 * - **From the bag.** An item that can be used from the bar (a consumable whose use the rules hold) dropped on an empty
 *   slot or an item's slot replaces what the slot held, and onto an ability's or a blow's slot it is refused (ADR-121's
 *   note for P2.8-T83); the item stays in the bag. Dropped off the bar it does nothing.
 * - **The lock** (decision 3.2, `odyssey.bar.locked`): while it is on, a drag from the bar starts only with Shift held,
 *   and a drop from the bag onto an occupied slot needs Shift; onto an empty slot it never does.
 * - **Placing** (decision 3.3): the keyboard's way to move a thing. Shift+Enter on a bar slot, Ctrl+Enter on a bag slot
 *   (ADR-121's note of 2026-10-08) or the bag's "Place on bar" picks it up; then Left and Right (wrapping), Home and End
 *   move focus along the bar, Enter places into the focused slot by the drop rule, Delete clears the focused slot (Shift
 *   and Delete while the lock is on) unless it holds an ability or a blow, and Escape cancels. It is a layer naming
 *   only its keys: the four arrows are its, so none of them moves the character, while W, A, S and D still do.
 * - **Where the bar pages** (decision 8, P2.8-T30): `page-follow.ts` says how the page shown follows placing and a
 *   drag. A drag's mark may name the swap control (`SWAP_TARGET`), which draws as a slot that takes the drop does.
 *
 * Nothing is arranged before the session's `bar` arrives or while the account is frozen (`ArrangedBar.canArrange`).
 */

/**
 * A drop's mark on a slot of the bar: the slot and whether a drop there would be taken.
 */
export interface BarOver {
  readonly index: number;
  readonly tone: DropTone;
}

/**
 * Placing from the keyboard: the bar slot it came from (null for a thing from the bag), what it carries, and the slot
 * focused.
 */
export interface Placing {
  readonly from: number | null;
  readonly thing: BarSlot;
  readonly focus: number;
}

/**
 * What the bar draws while it is arranged: the slot a drag from the bar carries (null for none), the slot a drag is held
 * over, and the keyboard's placing (null for none).
 */
export interface ArrangingSnapshot {
  readonly dragging: number | null;
  readonly over: BarOver | null;
  readonly placing: Placing | null;
}

/**
 * The keys placing names, beside Escape, which closes it.
 */
const PLACING_KEYS = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Enter", "Delete"] as const;

/**
 * Nothing arranging.
 */
const IDLE: ArrangingSnapshot = Object.freeze({ dragging: null, over: null, placing: null });

/**
 * The bag as arranging reads it: the item of the slot picked up (Ctrl+Enter or a click with Shift), or null, and a way
 * to put it back.
 */
export interface HeldBag {
  readonly held: () => string | null;
  readonly putBack: () => void;
}

/**
 * What arranging needs of the page's input: a way to open a layer.
 */
export interface ArrangingInput {
  open(options: LayerOptions): LayerHandle;
}

/**
 * The page's arranging of the bar.
 */
export class BarArranging {
  private snapshot: ArrangingSnapshot = IDLE;
  private readonly listeners = new Set<() => void>();
  private layer: LayerHandle | null = null;
  private placed: (() => void) | null = null;
  private bag: HeldBag | null = null;
  /**
   * How the page shown follows this arranging where the bar pages; a drag resting on the swap control turns it here.
   */
  public readonly follow: PageFollow;

  /**
   * @param arranged The player's bar.
   * @param rules The page's rules, which say whether an item can stand on the bar.
   * @param locked Whether the lock is on now.
   * @param input The page's input router, which placing opens its layer on; null where nothing places by keyboard.
   * @param pages The bar's pages, which placing's focus and a held drag turn; null where the bar never pages.
   * @param belt The tool belt, which takes the tools the bar refuses (ADR-142 decision 3b); null where there is none.
   */
  public constructor(
    private readonly arranged: Pick<ArrangedBar, "canArrange" | "current" | "place" | "swap" | "clear">,
    private readonly rules: Rules,
    private readonly locked: () => boolean,
    private readonly input: ArrangingInput | null,
    pages: ArrangingPages | null = null,
    public readonly belt: ToolBelt | null = null,
  ) {
    this.follow = new PageFollow(pages, () => this.snapshot.placing?.focus ?? null, (focus) => this.focusAt(focus));
  }

  /**
   * Ends a drag that was cancelled (Escape, a lost window, a cancelled pointer, a release on the swap control): its
   * marks go, nothing changes, and a paged bar shows the page the drag started on again.
   */
  public cancelDrag(): void {
    this.endDrag();
    this.follow.dragCancelled();
  }

  /**
   * Hears each change of what the bar draws while it is arranged.
   *
   * @param listener Called after a change.
   * @returns Stops it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the bar draws now.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): ArrangingSnapshot => this.snapshot;

  /**
   * Whether the bar may be arranged now.
   *
   * @returns True once the session's bar arrived, while the account is not frozen.
   */
  public canArrange(): boolean {
    return this.arranged.canArrange();
  }

  /**
   * Whether an item can stand on the bar: a consumable whose use the rules hold. A tool never does: the tool belt is the
   * only way to hold one (ADR-142 decision 3b), so a drop of a tool is refused.
   *
   * @param item The item's rules id.
   * @returns True for a placeable item.
   */
  public placeable(item: string): boolean {
    const known = this.rules.items.get(item);
    return known !== undefined && known.tool === undefined && known.kind === "consumable" && known.use !== null && this.rules.abilities.has(known.use);
  }

  /**
   * Names the bag whose picked-up slot a press on a bar slot places (ADR-121 decision 3.3: a thing picked up from the
   * bag, then a slot of the bar).
   *
   * @param bag The bag.
   */
  public bindBag(bag: HeldBag): void {
    this.bag = bag;
  }

  /**
   * The bag's picked-up item a press on a bar slot would place, or null.
   *
   * @returns The item's rules id.
   */
  public heldItem(): string | null {
    const item = this.bag?.held() ?? null;
    return item !== null && this.canArrange() && this.placeable(item) ? item : null;
  }

  /**
   * Places the bag's picked-up item in a bar slot by the drop rule, and puts the bag's slot back.
   *
   * @param index The slot, from 0.
   * @param shift Whether Shift is held.
   * @returns True when the bar changed.
   */
  public placeHeld(index: number, shift: boolean): boolean {
    const item = this.heldItem();
    if (item === null) {
      return false;
    }
    const placed = this.placeItem(index, item, shift);
    this.bag?.putBack();
    return placed;
  }

  /**
   * Starts a drag from a bar slot: one holding something, while the bar may be arranged, with Shift held while the lock
   * is on.
   *
   * @param index The slot, from 0.
   * @param shift Whether Shift is held.
   * @returns True when the drag starts.
   */
  public pick(index: number, shift: boolean): boolean {
    if (!this.canArrange() || (this.locked() && !shift) || this.thingAt(index) === null) {
      return false;
    }
    this.change({ dragging: index, over: null, placing: null });
    return true;
  }

  /**
   * Marks the bar slot a drag from the bar is held over: always taken, a swap.
   *
   * @param index The slot under the pointer, or null off the bar.
   */
  public hoverFromBar(index: number | null): void {
    const from = this.snapshot.dragging;
    if (from === null) {
      return;
    }
    this.setOver(index === null || index === from ? null : { index, tone: "positive" });
  }

  /**
   * Ends a drag from the bar where it was released: on another slot the two swap; off the bar an item clears, while an
   * ability or a weapon blow is put back, since the bar is where it lives (ADR-121's note of 2026-10-10, the author's
   * choice: "Dragging an ability or blow off the bar does nothing; it can only swap slots. Potions etc. still clear.");
   * on its own slot nothing changes.
   *
   * @param index The bar slot released over, or null for anywhere off the bar.
   * @returns True when the bar changed.
   */
  public dropFromBar(index: number | null): boolean {
    const from = this.snapshot.dragging;
    this.change(IDLE);
    this.follow.dragLanded();
    if (from === null) {
      return false;
    }
    if (index === null) {
      return !this.keeps(from) && this.arranged.clear(from);
    }
    return index !== from && this.arranged.swap(from, index);
  }

  /**
   * How a slot of the bar answers an item dragged from the bag: taken while the bar may be arranged and the item can
   * stand there, onto an empty slot always and onto an item's slot while the lock is off or Shift is held; never onto a
   * slot holding an ability or a blow, which an item would push off the bar (ADR-121's note for P2.8-T83).
   *
   * @param index The slot, from 0.
   * @param item The item's rules id.
   * @param shift Whether Shift is held.
   * @returns The tone of the drop.
   */
  public toneForItem(index: number, item: string, shift: boolean): DropTone {
    if (index === SWAP_TARGET) {
      return "positive";
    }
    if (!this.canArrange() || !this.placeable(item) || this.keeps(index)) {
      return "danger";
    }
    return this.thingAt(index) === null || !this.locked() || shift ? "positive" : "danger";
  }

  /**
   * Marks the bar slot a drag from the bag is held over.
   *
   * @param index The slot under the pointer, or null off the bar.
   * @param item The item dragged.
   * @param shift Whether Shift is held.
   */
  public hoverFromBag(index: number | null, item: string, shift: boolean): void {
    this.setOver(index === null ? null : { index, tone: this.toneForItem(index, item, shift) });
  }

  /**
   * Puts an item from the bag in a bar slot, by the drop rule; the item stays in the bag.
   *
   * @param index The slot, from 0.
   * @param item The item's rules id.
   * @param shift Whether Shift is held.
   * @returns True when the bar changed.
   */
  public placeItem(index: number, item: string, shift: boolean): boolean {
    this.setOver(null);
    this.follow.dragLanded();
    return this.toneForItem(index, item, shift) === "positive" && this.arranged.place(index, ["item", item]);
  }

  /**
   * Ends any drag's marks on the bar, changing nothing.
   */
  public endDrag(): void {
    if (this.snapshot.dragging !== null || this.snapshot.over !== null) {
      this.change({ ...this.snapshot, dragging: null, over: null });
    }
  }

  /**
   * Picks a bar slot up from the keyboard (Shift+Enter): the slot must hold something, and the lock asks nothing here,
   * since placing changes nothing until Enter or Delete.
   *
   * @param index The slot, from 0.
   * @returns True when placing starts.
   */
  public startPlacingSlot(index: number): boolean {
    const thing = this.thingAt(index);
    if (!this.canArrange() || thing === null) {
      return false;
    }
    this.startPlacing({ from: index, thing, focus: index }, null);
    return true;
  }

  /**
   * Picks an item from the bag up to place it on the bar (Ctrl+Enter, or "Place on bar"): focus goes to the first empty
   * slot, or slot 1.
   *
   * @param item The item's rules id.
   * @param done Told when placing ends, placed or cancelled, so the bag can put its slot back.
   * @returns True when placing starts.
   */
  public startPlacingItem(item: string, done: () => void): boolean {
    if (!this.canArrange() || !this.placeable(item)) {
      return false;
    }
    const slots = this.arranged.current();
    const empty = slots.findIndex((slot) => slot === null);
    this.startPlacing({ from: null, thing: ["item", item], focus: empty < 0 ? 0 : empty }, done);
    return true;
  }

  /**
   * Places what placing carries into a slot by the drop rule: from the bar the two slots swap, from the bag the slot
   * takes the item; then placing ends.
   *
   * @param index The slot, from 0.
   * @param shift Whether Shift is held, which the lock asks for onto an occupied slot.
   * @returns True when the bar changed.
   */
  public placeAt(index: number, shift: boolean): boolean {
    const placing = this.snapshot.placing;
    if (placing === null) {
      return false;
    }
    this.stopPlacing();
    if (placing.from !== null) {
      return index !== placing.from && this.arranged.swap(placing.from, index);
    }
    const thing = placing.thing;
    return thing !== null && thing[0] === "item" && this.placeItem(index, thing[1], shift);
  }

  /**
   * Clears the focused slot while placing (Delete), under the lock's rule, and ends placing; a slot holding an ability or
   * a blow is never cleared and placing stays (ADR-121's note for P2.8-T83).
   *
   * @param shift Whether Shift is held.
   * @returns True when the bar changed.
   */
  public clearAt(shift: boolean): boolean {
    const placing = this.snapshot.placing;
    if (placing === null || (this.locked() && !shift) || this.keeps(placing.focus)) {
      return false;
    }
    this.stopPlacing();
    return this.arranged.clear(placing.focus);
  }

  /**
   * Moves placing's focus: by a step along the bar, wrapping, or to an end.
   *
   * @param to `next`, `previous`, `first` or `last`.
   */
  public moveFocus(to: "next" | "previous" | "first" | "last"): void {
    const placing = this.snapshot.placing;
    if (placing === null) {
      return;
    }
    const focus = to === "first" ? 0 : to === "last" ? BAR_SLOTS - 1 : (placing.focus + (to === "next" ? 1 : BAR_SLOTS - 1)) % BAR_SLOTS;
    this.change({ ...this.snapshot, placing: { ...placing, focus } });
    this.follow.showFocus();
  }

  /**
   * Focuses a slot while placing, as a pointer's focus on it does.
   *
   * @param index The slot, from 0.
   */
  public focusAt(index: number): void {
    const placing = this.snapshot.placing;
    if (placing !== null && placing.focus !== index) {
      this.change({ ...this.snapshot, placing: { ...placing, focus: index } });
      this.follow.showFocus();
    }
  }

  /**
   * Ends placing, changing nothing (Escape, or a change of session).
   */
  public stopPlacing(): void {
    const layer = this.layer;
    this.layer = null;
    layer?.close();
    const done = this.placed;
    this.placed = null;
    if (this.snapshot.placing !== null) {
      this.change({ ...this.snapshot, placing: null });
    }
    done?.();
  }

  /**
   * What a slot holds as a change would start from.
   *
   * @param index The slot, from 0.
   * @returns The thing, or null for an empty slot.
   */
  public thingAt(index: number): BarSlot {
    return this.arranged.current()[index] ?? null;
  }

  /**
   * Whether a slot holds what never leaves the bar: an ability or a weapon blow (ADR-121's note for P2.8-T83, the
   * author's choice of 2026-10-10). Such a slot only swaps.
   *
   * @param index The slot, from 0.
   * @returns True for an ability or a blow.
   */
  private keeps(index: number): boolean {
    const thing = this.thingAt(index);
    return thing !== null && thing[0] !== "item";
  }

  /**
   * Starts placing and opens its layer.
   *
   * @param placing What it carries and where focus starts.
   * @param done Told when it ends, or null.
   */
  private startPlacing(placing: Placing, done: (() => void) | null): void {
    this.stopPlacing();
    this.placed = done;
    this.change({ dragging: null, over: null, placing });
    this.follow.showFocus();
    if (this.input === null) {
      return;
    }
    const keys: Record<string, (event: { readonly shiftKey?: boolean }) => unknown> = {};
    for (const key of PLACING_KEYS) {
      keys[key] = (event) => this.answer(key, event.shiftKey === true);
    }
    this.layer = this.input.open({
      onClose: () => {
        if (this.layer !== null) {
          this.layer = null;
          this.stopPlacing();
        }
      },
      keys,
    });
  }

  /**
   * Answers a key placing names.
   *
   * @param key The key.
   * @param shift Whether Shift is held.
   */
  private answer(key: (typeof PLACING_KEYS)[number], shift: boolean): void {
    const placing = this.snapshot.placing;
    if (placing === null) {
      return;
    }
    if (key === "ArrowRight") {
      this.moveFocus("next");
    } else if (key === "ArrowLeft") {
      this.moveFocus("previous");
    } else if (key === "Home") {
      this.moveFocus("first");
    } else if (key === "End") {
      this.moveFocus("last");
    } else if (key === "Enter") {
      this.placeAt(placing.focus, shift);
    } else if (key === "Delete") {
      this.clearAt(shift);
    }
  }

  /**
   * Replaces the drop mark.
   *
   * @param over The mark, or null.
   */
  private setOver(over: BarOver | null): void {
    const before = this.snapshot.over;
    if (before === over || (before !== null && over !== null && before.index === over.index && before.tone === over.tone)) {
      return;
    }
    this.change({ ...this.snapshot, over });
  }

  /**
   * Replaces the snapshot and tells every listener.
   *
   * @param next The snapshot.
   */
  private change(next: ArrangingSnapshot): void {
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
